/**
 * Hook for fetching contact module tab counts (All Contacts).
 * Provides counts for the contacts module landing page tabs.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/api-client';
import { useOrganizationStore } from '@/store/organization.store';

export interface ContactModuleCounts {
  allContacts: number;
}

interface UseContactModuleCountsOptions {
  enabled?: boolean;
}

// Query key for contact module counts
const contactModuleCountsKey = ['contact-module-counts'] as const;

/**
 * Fetches contact module counts
 */
async function fetchContactModuleCounts(): Promise<ContactModuleCounts> {
  return apiClient<ContactModuleCounts>('/contacts/module-counts');
}

/**
 * Hook for fetching contact module counts.
 * Returns count for "All Contacts".
 */
export function useContactModuleCounts({ enabled = true }: UseContactModuleCountsOptions = {}) {
  const queryClient = useQueryClient();
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const { data, isLoading, error } = useQuery({
    queryKey: contactModuleCountsKey,
    queryFn: fetchContactModuleCounts,
    enabled: enabled && !!currentOrganization?.id,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const counts = {
    allContacts: data?.allContacts,
  };

  /**
   * Invalidate and refetch counts
   */
  const refetchCounts = () => {
    queryClient.invalidateQueries({ queryKey: contactModuleCountsKey });
  };

  return {
    counts,
    isLoading,
    error,
    refetchCounts,
  };
}

// Export query key for external use
export { contactModuleCountsKey };
